import { useState, useEffect, useCallback } from "react";
import {
  Users, Plus, Trash2, X, CheckCircle, AlertCircle, RefreshCw,
  Dumbbell, Edit2, Search, ChevronRight, Activity, Award, ArrowLeft, Mail, Send, Salad,
} from "lucide-react";
import SporterVoedingTab from "./SporterVoedingTab";

const API = process.env.REACT_APP_BACKEND_URL;

function Toast({ msg, type, onClose }) {
  useEffect(() => { const t = setTimeout(onClose, 4000); return () => clearTimeout(t); }, [onClose]);
  return (
    <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-5 py-3.5 rounded-xl text-sm font-semibold shadow-2xl
      ${type === "success" ? "bg-[#111] border border-[#C9A84C]/40 text-white" : "bg-red-950 border border-red-500/40 text-red-300"}`}>
      {type === "success" ? <CheckCircle size={16} className="text-[#C9A84C]" /> : <AlertCircle size={16} className="text-red-400" />}
      {msg}
      <button onClick={onClose} className="ml-2 opacity-60 hover:opacity-100"><X size={14} /></button>
    </div>
  );
}

function CreateSporterModal({ token, onClose, onCreated }) {
  const [naam, setNaam] = useState("");
  const [email, setEmail] = useState("");
  const [plan, setPlan] = useState("schema");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!naam.trim()) { setError("Vul een naam in"); return; }
    setLoading(true); setError("");
    try {
      const r = await fetch(`${API}/api/company/sporters`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ naam, email, plan }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Aanmaken mislukt");
      setSuccess(d);
      onCreated();
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <div className="bg-[#111] border border-white/10 rounded-2xl p-7 max-w-md w-full animate-fadeIn">
        {success ? (
          <div className="text-center py-4">
            <CheckCircle size={40} className="text-[#C9A84C] mx-auto mb-3" />
            <h3 className="text-white font-bold text-lg mb-1">Sporter aangemaakt!</h3>
            {success.email && <p className="text-zinc-400 text-sm mb-2">{success.email}</p>}
            <p className="text-zinc-500 text-sm mb-6">
              {success.email
                ? "Er is een wachtwoord-instellen link verstuurd per e-mail."
                : "Sporter is zonder e-mail aangemaakt. Je kunt hem/haar trainen via Groep Modus."}
            </p>
            <button onClick={onClose} className="px-8 py-3 text-black font-bold text-sm rounded-xl" style={{ background: "#C9A84C" }}>
              Sluiten
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-white font-bold text-lg">Sporter toevoegen</h2>
              <button type="button" onClick={onClose} className="text-zinc-500 hover:text-white"><X size={18} /></button>
            </div>

            <label className="block text-zinc-400 text-xs font-semibold mb-1.5 uppercase tracking-wide">Naam</label>
            <input value={naam} onChange={e => setNaam(e.target.value)} placeholder="Emma de Vries"
              className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-4 py-3 text-white text-sm mb-4 focus:outline-none focus:border-[#C9A84C]/50"
              data-testid="sporter-naam-input" />

            <label className="block text-zinc-400 text-xs font-semibold mb-1.5 uppercase tracking-wide">
              E-mailadres <span className="text-zinc-600 normal-case font-normal">(optioneel)</span>
            </label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="emma@email.nl"
              className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-4 py-3 text-white text-sm mb-4 focus:outline-none focus:border-[#C9A84C]/50"
              data-testid="sporter-email-input" />

            <label className="block text-zinc-400 text-xs font-semibold mb-1.5 uppercase tracking-wide">Toegang</label>
            <div className="flex gap-2 mb-5">
              {[{ key: "schema", label: "Schema's" }].map(o => (
                <button key={o.key} type="button" onClick={() => setPlan(o.key)}
                  className={`flex-1 py-2.5 text-xs font-bold rounded-xl border transition-colors ${
                    plan === o.key ? "border-[#C9A84C] text-[#C9A84C] bg-[#C9A84C]/8" : "border-white/10 text-zinc-500 hover:border-white/20"
                  }`}>
                  {o.label}
                </button>
              ))}
            </div>

            {error && <p className="text-red-400 text-sm mb-4">{error}</p>}
            <div className="flex gap-3">
              <button type="button" onClick={onClose} className="flex-1 py-3 bg-zinc-800/50 border border-white/8 text-zinc-400 font-semibold text-sm rounded-xl">Annuleren</button>
              <button type="submit" disabled={loading} data-testid="create-sporter-submit-btn"
                className="flex-1 py-3 text-black font-bold text-sm rounded-xl disabled:opacity-40"
                style={{ background: "#C9A84C" }}>
                {loading ? "Aanmaken..." : "Sporter aanmaken"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function AssignSchemaModal({ token, sporter, onClose, onAssigned }) {
  const [schemas, setSchemas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(null);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/company/custom-schemas`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => setSchemas(d.schemas || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);

  const assignedIds = sporter.custom_schema_ids || [];

  const handleAssign = async (schemaId) => {
    setAssigning(schemaId);
    try {
      const r = await fetch(`${API}/api/company/custom-schemas/${schemaId}/assign`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ sporter_id: sporter.id }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Toewijzen mislukt");
      setToast({ msg: "Schema toegewezen!", type: "success" });
      onAssigned();
    } catch (err) { setToast({ msg: err.message, type: "error" }); }
    finally { setAssigning(null); }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <div className="bg-[#111] border border-white/10 rounded-2xl p-6 max-w-md w-full max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between mb-4 flex-shrink-0">
          <div>
            <h2 className="text-white font-bold">Schema toewijzen</h2>
            <p className="text-zinc-500 text-sm">aan {sporter.naam}</p>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X size={18} /></button>
        </div>

        <div className="overflow-y-auto flex-1">
          {loading ? (
            <div className="text-center py-8 text-zinc-600"><RefreshCw size={18} className="animate-spin mx-auto" /></div>
          ) : schemas.length === 0 ? (
            <div className="text-center py-8">
              <Dumbbell size={32} className="text-zinc-700 mx-auto mb-2" />
              <p className="text-zinc-500 text-sm">Geen schema's aangemaakt</p>
              <p className="text-zinc-600 text-xs mt-1">Maak eerst schema's aan in het Schema's tabblad.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {schemas.map(s => {
                const isAssigned = assignedIds.includes(s.id);
                return (
                  <div key={s.id} className={`flex items-center justify-between p-3.5 rounded-xl border transition-colors ${
                    isAssigned ? "border-[#C9A84C]/30 bg-[#C9A84C]/5" : "border-white/5 bg-[#0f0f0f] hover:border-white/10"
                  }`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${isAssigned ? "bg-[#C9A84C]/15" : "bg-white/5"}`}>
                        {isAssigned ? <CheckCircle size={14} className="text-[#C9A84C]" /> : <Dumbbell size={14} className="text-zinc-500" />}
                      </div>
                      <div className="min-w-0">
                        <p className="text-white text-sm font-medium truncate">{s.naam}</p>
                        {s.doel && <p className="text-zinc-500 text-xs">{s.doel}</p>}
                      </div>
                    </div>
                    {isAssigned ? (
                      <span className="text-[10px] font-bold text-[#C9A84C] px-2 py-1 rounded-full bg-[#C9A84C]/10 flex-shrink-0">Toegewezen</span>
                    ) : (
                      <button
                        onClick={() => handleAssign(s.id)}
                        disabled={assigning === s.id}
                        data-testid={`assign-schema-${s.id}`}
                        className="flex-shrink-0 text-xs font-bold px-3 py-1.5 rounded-lg text-black disabled:opacity-50 transition-opacity"
                        style={{ background: "#C9A84C" }}>
                        {assigning === s.id ? "..." : "Toewijzen"}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <button onClick={onClose} className="mt-4 w-full py-2.5 bg-zinc-800/50 border border-white/8 text-zinc-400 font-semibold text-sm rounded-xl flex-shrink-0">
          Sluiten
        </button>

        {toast && <Toast msg={toast.msg} type={toast.type} onClose={() => setToast(null)} />}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const MAP = {
    voltooid:       { label: "Nieuw schema nodig", cls: "bg-red-500/15 text-red-400 border-red-500/25" },
    bijna_voltooid: { label: "Bijna voltooid",      cls: "bg-yellow-500/15 text-yellow-400 border-yellow-500/25" },
    actief:         { label: "Actief",               cls: "bg-green-500/15 text-green-400 border-green-500/25" },
    geen_schema:    { label: "Geen schema",          cls: "bg-zinc-700/30 text-zinc-500 border-zinc-600/25" },
  };
  const s = MAP[status] || MAP.geen_schema;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold border ${s.cls}`}>
      {s.label}
    </span>
  );
}

function ProgressBar({ count, doelstelling }) {
  const pct = Math.min(100, Math.round((count / doelstelling) * 100));
  const kleur = pct >= 100 ? "#ef4444" : pct >= 75 ? "#eab308" : "#C9A84C";
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-white/8 rounded-full overflow-hidden min-w-16">
        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: kleur }} />
      </div>
      <span className="text-xs text-zinc-400 tabular-nums shrink-0">{count}/{doelstelling}</span>
    </div>
  );
}

function SporterDetailPanel({ token, sporter, trainers = [], onClose, onRefresh }) {
  const [stats, setStats] = useState(null);
  const [progress, setProgress] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("profiel"); // "profiel" | "voeding"
  const [showAssign, setShowAssign] = useState(false);
  const [unassigning, setUnassigning] = useState(null);
  const [toast, setToast] = useState(null);
  const [medewerkerSaving, setMedewerkerSaving] = useState(false);
  const [resetConfirm, setResetConfirm] = useState(null);
  const [doelstellingEdit, setDoelstellingEdit] = useState(null);
  const [localMedewerker, setLocalMedewerker] = useState(sporter.medewerker_id || "");
  const [intake, setIntake] = useState(null);
  const [intakeLoading, setIntakeLoading] = useState(true);
  const [showIntakeEdit, setShowIntakeEdit] = useState(false);
  const [intakeEdit, setIntakeEdit] = useState({});
  const [intakeSaving, setIntakeSaving] = useState(false);
  const [emailEdit, setEmailEdit] = useState("");
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [emailSaving, setEmailSaving] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [localEmail, setLocalEmail] = useState(sporter.email || "");

  const loadStats = useCallback(async () => {
    setLoading(true);
    try {
      const auth = { headers: { Authorization: `Bearer ${token}` } };
      const [sRes, pRes] = await Promise.all([
        fetch(`${API}/api/company/sporters/${sporter.id}/stats`, auth),
        fetch(`${API}/api/company/sporters/${sporter.id}/progress`, auth),
      ]);
      if (sRes.ok) setStats(await sRes.json());
      if (pRes.ok) setProgress(await pRes.json());
    } catch {}
    finally { setLoading(false); }
  }, [token, sporter.id]);

  const loadIntake = useCallback(async () => {
    setIntakeLoading(true);
    try {
      const r = await fetch(`${API}/api/company/sporters/${sporter.id}/intake`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) {
        const d = await r.json();
        setIntake(d);
        setIntakeEdit(d.antwoorden || {});
      }
    } catch {}
    finally { setIntakeLoading(false); }
  }, [token, sporter.id]);

  useEffect(() => { loadStats(); loadIntake(); }, [loadStats, loadIntake]);

  const handleUnassign = async (schemaId) => {
    setUnassigning(schemaId);
    try {
      const r = await fetch(`${API}/api/company/sporters/${sporter.id}/schemas/${schemaId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error("Verwijderen mislukt");
      setToast({ msg: "Schema verwijderd", type: "success" });
      loadStats();
      onRefresh();
    } catch (err) { setToast({ msg: err.message, type: "error" }); }
    finally { setUnassigning(null); }
  };

  const handleAssignMedewerker = async (medewerker_id) => {
    setMedewerkerSaving(true);
    try {
      const r = await fetch(`${API}/api/company/sporters/${sporter.id}/medewerker`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ medewerker_id: medewerker_id || null }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Toewijzen mislukt");
      setLocalMedewerker(medewerker_id || "");
      setToast({ msg: medewerker_id ? "Medewerker gekoppeld!" : "Medewerker ontkoppeld", type: "success" });
      onRefresh();
    } catch (err) { setToast({ msg: err.message, type: "error" }); }
    finally { setMedewerkerSaving(false); }
  };

  const handleResetProgress = async (schema_id) => {
    try {
      const r = await fetch(`${API}/api/company/sporters/${sporter.id}/progress/${schema_id}/reset`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error("Reset mislukt");
      setResetConfirm(null);
      setToast({ msg: "Voortgang gereset!", type: "success" });
      loadStats();
      onRefresh();
    } catch (err) { setToast({ msg: err.message, type: "error" }); }
  };

  const handleUpdateDoelstelling = async (schema_id, doelstelling) => {
    try {
      const r = await fetch(`${API}/api/company/custom-schemas/${schema_id}/doelstelling`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ doelstelling: parseInt(doelstelling) || 8 }),
      });
      if (!r.ok) throw new Error("Bijwerken mislukt");
      setDoelstellingEdit(null);
      setToast({ msg: "Doelstelling bijgewerkt!", type: "success" });
      loadStats();
    } catch (err) { setToast({ msg: err.message, type: "error" }); }
  };

  const handleSaveIntake = async () => {
    setIntakeSaving(true);
    try {
      const r = await fetch(`${API}/api/company/sporters/${sporter.id}/intake`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(intakeEdit),
      });
      if (!r.ok) throw new Error("Opslaan mislukt");
      setShowIntakeEdit(false);
      setToast({ msg: "Intake opgeslagen!", type: "success" });
      loadIntake();
    } catch (err) { setToast({ msg: err.message, type: "error" }); }
    finally { setIntakeSaving(false); }
  };

  const handleSaveEmail = async (stuurMail) => {
    if (!emailEdit.trim()) return;
    setEmailSaving(true);
    try {
      const r = await fetch(`${API}/api/company/sporters/${sporter.id}/email`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ email: emailEdit.trim(), stuur_welkomstmail: stuurMail }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Opslaan mislukt");
      setLocalEmail(d.email);
      setShowEmailForm(false);
      setEmailEdit("");
      setToast({ msg: stuurMail ? "E-mail opgeslagen & welkomstmail verstuurd!" : "E-mail opgeslagen!", type: "success" });
      onRefresh();
    } catch (err) { setToast({ msg: err.message, type: "error" }); }
    finally { setEmailSaving(false); }
  };

  const handleResendUitnodiging = async () => {
    setResendLoading(true);
    try {
      const r = await fetch(`${API}/api/company/sporters/${sporter.id}/resend-uitnodiging`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Versturen mislukt");
      setToast({ msg: `Uitnodiging verstuurd naar ${d.email}!`, type: "success" });
    } catch (err) {
      setToast({ msg: err.message, type: "error" });
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-[#111] border border-white/10 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-lg max-h-[90vh] flex flex-col">
        <div className="flex items-center gap-3 p-5 border-b border-white/5 flex-shrink-0">
          <button onClick={onClose} className="text-zinc-500 hover:text-white mr-1"><ArrowLeft size={18} /></button>
          <div className="w-10 h-10 rounded-xl flex items-center justify-center text-sm font-black text-black flex-shrink-0"
            style={{ background: "#C9A84C" }}>
            {(sporter.naam || "?").charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white font-bold truncate">{sporter.naam}</p>
            {localEmail
              ? <p className="text-zinc-500 text-xs truncate">{localEmail}</p>
              : <p className="text-amber-500/80 text-xs">Geen e-mail ingesteld</p>
            }
          </div>
          <span className={`text-[10px] font-bold px-2 py-1 rounded-full uppercase flex-shrink-0 ${
            sporter.plan === "full" ? "bg-blue-500/10 text-blue-400" : "bg-amber-500/10 text-amber-400"
          }`}>{sporter.plan === "full" ? "Cursus" : "Schema"}</span>
        </div>

        <div className="overflow-y-auto flex-1 p-5 space-y-4">
          {/* Tab navigatie */}
          <div className="flex gap-1 bg-[#0f0f0f] border border-white/6 rounded-xl p-1">
            {[
              { key: "profiel", label: "Profiel", icon: <Dumbbell size={12}/> },
              { key: "voeding", label: "Voeding", icon: <Salad size={12}/> },
            ].map(t => (
              <button key={t.key} onClick={() => setActiveTab(t.key)}
                data-testid={`sporter-tab-${t.key}`}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-bold transition-all ${
                  activeTab === t.key
                    ? "bg-[#C9A84C] text-black"
                    : "text-zinc-500 hover:text-white"
                }`}>
                {t.icon} {t.label}
              </button>
            ))}
          </div>

          {activeTab === "voeding" ? (
            <SporterVoedingTab token={token} sporter={sporter} />
          ) : loading ? (
            <div className="grid grid-cols-3 gap-3">
              {[1,2,3].map(i => <div key={i} className="h-16 bg-white/5 rounded-xl animate-pulse" />)}
            </div>
          ) : stats && (
            <>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: "Schema's", val: stats.toegewezen_schemas?.length ?? 0, icon: <Dumbbell size={14} />, testid: "stat-schemas" },
                  { label: "Trainingen", val: stats.workout_log_count ?? 0, icon: <Activity size={14} />, testid: "stat-trainingen" },
                  { label: "Aangemeld", val: stats.aangemeld_sinds ? new Date(stats.aangemeld_sinds).toLocaleDateString("nl-NL", { day: "numeric", month: "short" }) : "–", icon: <Award size={14} />, small: true, testid: "stat-aangemeld" },
                ].map(({ label, val, icon, small, testid }) => (
                  <div key={label} className="bg-[#0f0f0f] border border-white/5 rounded-xl p-3 text-center" data-testid={testid}>
                    <div className="text-[#C9A84C] flex justify-center mb-1">{icon}</div>
                    <p className={`font-bold text-white leading-tight ${small ? "text-sm" : "text-xl"}`}>{val}</p>
                    <p className="text-zinc-600 text-[10px] mt-0.5">{label}</p>
                  </div>
                ))}
              </div>

              {/* E-mail toevoegen banner (alleen als geen email) */}
              {!localEmail && (
                <div className="bg-amber-500/8 border border-amber-500/20 rounded-xl p-4">
                  {!showEmailForm ? (
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <Mail size={15} className="text-amber-400 shrink-0" />
                        <div className="min-w-0">
                          <p className="text-amber-300 text-sm font-semibold">Geen e-mail ingesteld</p>
                          <p className="text-amber-500/70 text-xs">Voeg een e-mail toe zodat de sporter kan inloggen</p>
                        </div>
                      </div>
                      <button
                        onClick={() => setShowEmailForm(true)}
                        data-testid="add-email-btn"
                        className="shrink-0 px-3 py-1.5 bg-amber-500 text-black text-xs font-bold rounded-lg hover:bg-amber-400 transition-colors"
                      >
                        Toevoegen
                      </button>
                    </div>
                  ) : (
                    <div>
                      <p className="text-amber-300 text-xs font-semibold uppercase tracking-wide mb-2.5">E-mailadres toevoegen</p>
                      <input
                        type="email"
                        value={emailEdit}
                        onChange={e => setEmailEdit(e.target.value)}
                        placeholder="naam@email.nl"
                        data-testid="email-edit-input"
                        className="w-full bg-[#0f0f0f] border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm mb-3 focus:outline-none focus:border-amber-500/40"
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={() => { setShowEmailForm(false); setEmailEdit(""); }}
                          className="px-3 py-2 text-xs text-zinc-500 border border-white/8 rounded-lg hover:text-white transition-colors"
                        >
                          Annuleren
                        </button>
                        <button
                          onClick={() => handleSaveEmail(false)}
                          disabled={emailSaving || !emailEdit.trim()}
                          data-testid="save-email-btn"
                          className="flex-1 py-2 text-xs font-bold text-black rounded-lg disabled:opacity-40 transition-colors bg-zinc-300 hover:bg-white"
                        >
                          Opslaan
                        </button>
                        <button
                          onClick={() => handleSaveEmail(true)}
                          disabled={emailSaving || !emailEdit.trim()}
                          data-testid="save-email-send-btn"
                          className="flex-1 py-2 text-xs font-bold text-black rounded-lg disabled:opacity-40 flex items-center justify-center gap-1.5 transition-colors"
                          style={{ background: "#C9A84C" }}
                        >
                          <Send size={11} /> Opslaan & verstuur welkomstmail
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Uitnodiging opnieuw sturen (alleen als sporter een email heeft) */}
              {localEmail && (
                <div className="bg-[#0f0f0f] border border-white/6 rounded-xl p-3.5 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <Send size={14} className="text-zinc-500 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-zinc-300 text-sm font-semibold">Uitnodiging opnieuw sturen</p>
                      <p className="text-zinc-600 text-xs truncate">Stuurt nieuwe inloggegevens naar {localEmail}</p>
                    </div>
                  </div>
                  <button
                    onClick={handleResendUitnodiging}
                    disabled={resendLoading}
                    data-testid="resend-uitnodiging-btn"
                    className="shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold transition-all disabled:opacity-40"
                    style={{ background: "#C9A84C", color: "#000" }}
                  >
                    {resendLoading
                      ? <RefreshCw size={11} className="animate-spin" />
                      : <Send size={11} />
                    }
                    {resendLoading ? "Sturen..." : "Sturen"}
                  </button>
                </div>
              )}

              {/* Medewerker koppeling */}
              <div>
                <p className="text-zinc-400 text-xs font-semibold uppercase tracking-wide mb-2">Gekoppelde medewerker</p>
                <div className="flex items-center gap-2">
                  <select
                    value={localMedewerker}
                    onChange={e => handleAssignMedewerker(e.target.value)}
                    disabled={medewerkerSaving}
                    className="flex-1 bg-[#0f0f0f] border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#C9A84C]/30 disabled:opacity-50"
                    data-testid="medewerker-select"
                  >
                    <option value="">Geen medewerker</option>
                    {trainers.map(t => (
                      <option key={t.id} value={t.id}>{t.naam}</option>
                    ))}
                  </select>
                  {medewerkerSaving && <RefreshCw size={14} className="animate-spin text-zinc-500 flex-shrink-0" />}
                </div>
              </div>

              {/* Intake sectie */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-zinc-400 text-xs font-semibold uppercase tracking-wide">Intake / Profiel</p>
                  <button
                    onClick={() => { setIntakeEdit(intake?.antwoorden || {}); setShowIntakeEdit(true); }}
                    className="text-[10px] text-zinc-500 hover:text-[#C9A84C] transition-colors flex items-center gap-1"
                    data-testid="edit-intake-btn"
                  >
                    <Edit2 size={10} /> Aanpassen
                  </button>
                </div>
                {intakeLoading ? (
                  <div className="h-10 bg-white/3 rounded-xl animate-pulse" />
                ) : intake?.heeft_intake ? (
                  <div className="bg-[#0f0f0f] border border-white/5 rounded-xl p-3 space-y-2">
                    {[
                      { label: "Trainingsdoel", key: "trainingsdoel", icon: "🎯" },
                      { label: "Klachten / blessures", key: "klachten", icon: "🩺" },
                      { label: "Ervaring", key: "trainingservaring", icon: "💪" },
                      { label: "Trainingsdagen/week", key: "trainingsdagen", icon: "📅" },
                      { label: "Gewicht", key: "gewicht", icon: "⚖️", suffix: "kg" },
                      { label: "Lengte", key: "lengte", icon: "📏", suffix: "cm" },
                      { label: "Medicatie", key: "medicatie", icon: "💊" },
                      { label: "Overige info", key: "overige_info", icon: "📝" },
                    ].map(({ label, key, icon, suffix }) => {
                      const val = intake.antwoorden?.[key];
                      if (!val) return null;
                      return (
                        <div key={key} className="flex items-start gap-2 text-xs">
                          <span className="text-sm leading-tight mt-0.5">{icon}</span>
                          <div className="min-w-0">
                            <p className="text-zinc-500 text-[10px] leading-tight">{label}</p>
                            <p className="text-white font-medium leading-snug mt-0.5">
                              {val}{suffix ? ` ${suffix}` : ""}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                    {intake.bijgewerkt_op && (
                      <p className="text-zinc-700 text-[10px] pt-1 border-t border-white/5">
                        Bijgewerkt: {new Date(intake.bijgewerkt_op).toLocaleDateString("nl-NL")}
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="bg-[#0f0f0f] border border-white/5 rounded-xl p-3 text-center">
                    <p className="text-zinc-600 text-xs">Nog geen intake ingevuld</p>
                    <button
                      onClick={() => { setIntakeEdit({}); setShowIntakeEdit(true); }}
                      className="mt-2 text-xs text-[#C9A84C] hover:underline"
                      data-testid="create-intake-btn"
                    >
                      Zelf invullen →
                    </button>
                  </div>
                )}
              </div>

              {/* Voortgang per schema */}
              {progress.length > 0 && (
                <div>
                  <p className="text-zinc-400 text-xs font-semibold uppercase tracking-wide mb-2">Voortgang per schema</p>
                  <div className="space-y-2">
                    {progress.map(p => (
                      <div key={p.schema_id} className="bg-[#0f0f0f] border border-white/5 rounded-xl p-3">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-white text-sm font-medium truncate flex-1 mr-2">{p.schema_naam}</p>
                          <StatusBadge status={p.status} />
                        </div>
                        <ProgressBar count={p.count} doelstelling={p.doelstelling} />
                        <div className="flex items-center justify-between mt-2">
                          <button
                            onClick={() => setDoelstellingEdit({ schema_id: p.schema_id, schema_naam: p.schema_naam, value: p.doelstelling })}
                            className="text-[10px] text-zinc-500 hover:text-zinc-300 transition-colors flex items-center gap-1"
                            data-testid={`edit-doelstelling-${p.schema_id}`}
                          >
                            <Edit2 size={10} /> Doel: {p.doelstelling}x
                          </button>
                          {p.count > 0 && (
                            <button
                              onClick={() => setResetConfirm(p)}
                              className="text-[10px] text-zinc-600 hover:text-red-400 transition-colors"
                              data-testid={`reset-progress-${p.schema_id}`}
                            >
                              Reset teller
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Toegewezen schema's */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-zinc-400 text-xs font-semibold uppercase tracking-wide">Toegewezen schema's</p>
                  <button onClick={() => setShowAssign(true)} data-testid="assign-schema-btn"
                    className="flex items-center gap-1.5 text-xs font-bold text-[#C9A84C] hover:text-[#C9A84C]/80 transition-colors">
                    <Plus size={12} /> Schema toewijzen
                  </button>
                </div>

                {stats.toegewezen_schemas?.length === 0 ? (
                  <div className="text-center py-6 border border-white/5 rounded-xl">
                    <Dumbbell size={24} className="text-zinc-700 mx-auto mb-2" />
                    <p className="text-zinc-500 text-sm">Nog geen schema's toegewezen</p>
                    <button onClick={() => setShowAssign(true)}
                      className="mt-3 text-xs font-bold px-4 py-2 rounded-xl text-black"
                      style={{ background: "#C9A84C" }}>
                      Schema toewijzen
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {stats.toegewezen_schemas.map(s => (
                      <div key={s.id} className="flex items-center justify-between p-3 bg-[#0f0f0f] border border-white/5 rounded-xl">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-[#C9A84C]/10 flex-shrink-0">
                            <Dumbbell size={12} className="text-[#C9A84C]" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-white text-sm font-medium truncate">{s.naam}</p>
                            {(s.doel || s.niveau) && (
                              <p className="text-zinc-600 text-xs">{[s.doel, s.niveau].filter(Boolean).join(" · ")}</p>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={() => handleUnassign(s.id)}
                          disabled={unassigning === s.id}
                          className="p-1.5 text-zinc-700 hover:text-red-400 transition-colors flex-shrink-0"
                          title="Schema verwijderen">
                          {unassigning === s.id ? <RefreshCw size={12} className="animate-spin" /> : <Trash2 size={12} />}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Recente trainingen */}
              {stats.recente_trainingen?.length > 0 && (
                <div>
                  <p className="text-zinc-400 text-xs font-semibold uppercase tracking-wide mb-2">Recente trainingen</p>
                  <div className="space-y-2">
                    {stats.recente_trainingen.map((t, i) => (
                      <div key={t.id || i} className="p-2.5 bg-[#0f0f0f] border border-white/5 rounded-xl">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 min-w-0">
                            <div className="w-1.5 h-1.5 rounded-full bg-[#C9A84C] flex-shrink-0" />
                            <p className="text-zinc-300 text-sm truncate">{t.schema_naam || "Training"}</p>
                          </div>
                          <p className="text-zinc-600 text-xs flex-shrink-0">
                            {t.logged_at ? new Date(t.logged_at).toLocaleDateString("nl-NL", { day: "numeric", month: "short" }) : "–"}
                          </p>
                        </div>
                        {t.opmerkingen?.length > 0 && (
                          <div className="mt-1.5 space-y-0.5 pl-3.5" data-testid={`sporter-training-opmerkingen-${i}`}>
                            {t.opmerkingen.map((o, oi) => (
                              <p key={oi} className="text-xs text-zinc-500 italic">
                                <span className="text-[#C9A84C] not-italic font-semibold">{o.oefening}:</span> {o.notitie}
                              </p>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="p-5 border-t border-white/5 flex-shrink-0">
          <button onClick={onClose} className="w-full py-2.5 bg-zinc-800/50 border border-white/8 text-zinc-400 font-semibold text-sm rounded-xl">
            Sluiten
          </button>
        </div>

        {showAssign && stats && (
          <AssignSchemaModal
            token={token}
            sporter={{ ...sporter, custom_schema_ids: stats.toegewezen_schemas?.map(s => s.id) || [] }}
            onClose={() => setShowAssign(false)}
            onAssigned={() => { loadStats(); onRefresh(); }}
          />
        )}

        {toast && <Toast msg={toast.msg} type={toast.type} onClose={() => setToast(null)} />}

        {/* Intake bewerken modal */}
        {showIntakeEdit && (
          <div className="fixed inset-0 bg-black/70 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4">
            <div className="bg-[#111] border border-white/10 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md max-h-[90vh] flex flex-col">
              <div className="flex items-center justify-between p-5 border-b border-white/5 flex-shrink-0">
                <div>
                  <h3 className="text-white font-bold">Intake aanpassen</h3>
                  <p className="text-zinc-500 text-xs mt-0.5">{sporter.naam}</p>
                </div>
                <button onClick={() => setShowIntakeEdit(false)} className="text-zinc-500 hover:text-white"><X size={18} /></button>
              </div>
              <div className="overflow-y-auto flex-1 p-5 space-y-3">
                {[
                  { label: "Trainingsdoel", key: "trainingsdoel", placeholder: "bijv. Afvallen, Spiermassa, Conditie...", type: "textarea" },
                  { label: "Klachten / blessures", key: "klachten", placeholder: "bijv. Knieklachten, rugpijn of geen klachten", type: "textarea" },
                  { label: "Trainingservaring", key: "trainingservaring", placeholder: "bijv. Beginner, 2 jaar ervaring..." },
                  { label: "Trainingsdagen per week", key: "trainingsdagen", placeholder: "bijv. 3", type: "number" },
                  { label: "Gewicht (kg)", key: "gewicht", placeholder: "bijv. 75", type: "number" },
                  { label: "Lengte (cm)", key: "lengte", placeholder: "bijv. 178", type: "number" },
                  { label: "Medicatie", key: "medicatie", placeholder: "bijv. Geen of omschrijving" },
                  { label: "Overige informatie", key: "overige_info", placeholder: "Extra aandachtspunten...", type: "textarea" },
                ].map(({ label, key, placeholder, type }) => (
                  <div key={key}>
                    <label className="block text-zinc-500 text-[10px] font-bold uppercase tracking-widest mb-1.5">{label}</label>
                    {type === "textarea" ? (
                      <textarea
                        value={intakeEdit[key] || ""}
                        onChange={e => setIntakeEdit(prev => ({ ...prev, [key]: e.target.value }))}
                        placeholder={placeholder}
                        rows={2}
                        className="w-full bg-[#1a1a1a] border border-white/8 rounded-xl px-3 py-2.5 text-white text-sm resize-none focus:outline-none focus:border-[#C9A84C]/40"
                        data-testid={`intake-field-${key}`}
                      />
                    ) : (
                      <input
                        type={type || "text"}
                        value={intakeEdit[key] || ""}
                        onChange={e => setIntakeEdit(prev => ({ ...prev, [key]: e.target.value }))}
                        placeholder={placeholder}
                        className="w-full bg-[#1a1a1a] border border-white/8 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-[#C9A84C]/40"
                        data-testid={`intake-field-${key}`}
                      />
                    )}
                  </div>
                ))}
              </div>
              <div className="p-5 border-t border-white/5 flex-shrink-0 flex gap-3">
                <button onClick={() => setShowIntakeEdit(false)} className="flex-1 py-3 rounded-xl text-sm font-bold border border-white/8 text-zinc-400">
                  Annuleren
                </button>
                <button
                  onClick={handleSaveIntake}
                  disabled={intakeSaving}
                  data-testid="save-intake-btn"
                  className="flex-1 py-3 rounded-xl text-black font-bold text-sm disabled:opacity-60"
                  style={{ background: "#C9A84C" }}
                >
                  {intakeSaving ? "Opslaan..." : "Opslaan"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Doelstelling bewerken modal */}
        {doelstellingEdit && (
          <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4">
            <div className="bg-[#111] border border-white/10 rounded-2xl p-6 max-w-xs w-full">
              <h3 className="text-white font-bold mb-1">Doelstelling aanpassen</h3>
              <p className="text-zinc-500 text-sm mb-4 truncate">{doelstellingEdit.schema_naam}</p>
              <p className="text-zinc-400 text-xs mb-3">Hoeveel sessies is de doelstelling voor dit schema?</p>
              <input
                type="number" min="1" max="100"
                value={doelstellingEdit.value}
                onChange={e => setDoelstellingEdit({ ...doelstellingEdit, value: parseInt(e.target.value) || 1 })}
                className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-4 py-3 text-white text-center text-2xl font-bold mb-4 focus:outline-none focus:border-[#C9A84C]/50"
                data-testid="doelstelling-input"
              />
              <div className="flex gap-3">
                <button onClick={() => setDoelstellingEdit(null)} className="flex-1 py-2.5 bg-zinc-800/50 border border-white/8 text-zinc-400 font-semibold text-sm rounded-xl">
                  Annuleren
                </button>
                <button
                  onClick={() => handleUpdateDoelstelling(doelstellingEdit.schema_id, doelstellingEdit.value)}
                  className="flex-1 py-2.5 text-black font-bold text-sm rounded-xl"
                  style={{ background: "#C9A84C" }}
                  data-testid="save-doelstelling-btn"
                >
                  Opslaan
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Voortgang reset bevestiging */}
        {resetConfirm && (
          <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4">
            <div className="bg-[#111] border border-white/10 rounded-2xl p-6 max-w-xs w-full">
              <h3 className="text-white font-bold mb-2">Voortgang resetten?</h3>
              <p className="text-zinc-400 text-sm mb-1">
                De teller van <strong className="text-white">{resetConfirm.schema_naam}</strong> wordt teruggezet naar 0.
              </p>
              <p className="text-zinc-600 text-xs mb-6">De trainingslogboeken voor dit schema worden ook verwijderd.</p>
              <div className="flex gap-3">
                <button onClick={() => setResetConfirm(null)} className="flex-1 py-2.5 bg-zinc-800/50 border border-white/8 text-zinc-400 font-semibold text-sm rounded-xl">
                  Annuleren
                </button>
                <button
                  onClick={() => handleResetProgress(resetConfirm.schema_id)}
                  className="flex-1 py-2.5 bg-red-900/40 border border-red-500/30 text-red-400 font-semibold text-sm rounded-xl"
                  data-testid="confirm-reset-btn"
                >
                  Reset
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function SportersTab({ token }) {
  const [sporters, setSporters] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [selectedSporter, setSelectedSporter] = useState(null);
  const [toast, setToast] = useState(null);
  const [search, setSearch] = useState("");
  const [filterMedewerker, setFilterMedewerker] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  const showToast = (msg, type = "success") => setToast({ msg, type });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const auth = { headers: { Authorization: `Bearer ${token}` } };
      const [ovRes, trRes] = await Promise.all([
        fetch(`${API}/api/company/sporters-overzicht`, auth),
        fetch(`${API}/api/company/personeel`, auth),
      ]);
      const ovData = await ovRes.json();
      const trData = await trRes.json();
      setSporters(Array.isArray(ovData) ? ovData : []);
      setTrainers(Array.isArray(trData) ? trData : (trData?.personeel || []));
    } catch { showToast("Laden mislukt", "error"); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    try {
      const r = await fetch(`${API}/api/company/sporters/${id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error("Verwijderen mislukt");
      setDeleteConfirm(null);
      showToast("Sporter verwijderd");
      load();
    } catch (err) { showToast(err.message, "error"); }
  };

  const filtered = sporters.filter(s => {
    const matchSearch = !search ||
      s.naam?.toLowerCase().includes(search.toLowerCase()) ||
      s.email?.toLowerCase().includes(search.toLowerCase());
    const matchMedewerker = !filterMedewerker ||
      (filterMedewerker === "__geen__" ? !s.medewerker_id : s.medewerker_id === filterMedewerker);
    const matchStatus = !filterStatus || s.overall_status === filterStatus;
    return matchSearch && matchMedewerker && matchStatus;
  });

  const selectCls = "bg-[#111] border border-white/8 rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-[#C9A84C]/30";

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div>
          <p className="text-[#C9A84C] text-xs font-bold uppercase tracking-widest mb-1">Beheer</p>
          <h2 className="text-xl font-bold text-white">Sporters</h2>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={load} className="p-2 text-zinc-500 hover:text-white transition-colors">
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
          </button>
          <button onClick={() => setShowCreate(true)} data-testid="add-sporter-btn"
            className="flex items-center gap-2 px-4 py-2.5 text-black font-bold text-sm rounded-xl" style={{ background: "#C9A84C" }}>
            <Plus size={14} /> Sporter toevoegen
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-48">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Zoek naam of e-mail..."
            className="w-full bg-[#111] border border-white/8 rounded-xl pl-9 pr-4 py-2 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-[#C9A84C]/30" />
        </div>
        <select value={filterMedewerker} onChange={e => setFilterMedewerker(e.target.value)} className={selectCls}>
          <option value="">Alle medewerkers</option>
          <option value="__geen__">Niet gekoppeld</option>
          {trainers.map(t => <option key={t.id} value={t.id}>{t.naam}</option>)}
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className={selectCls}>
          <option value="">Alle statussen</option>
          <option value="actief">Actief</option>
          <option value="bijna_voltooid">Bijna voltooid</option>
          <option value="voltooid">Nieuw schema nodig</option>
          <option value="geen_schema">Geen schema</option>
        </select>
      </div>

      {loading ? (
        <div className="text-center py-16 text-zinc-600"><RefreshCw size={20} className="animate-spin mx-auto" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16">
          <Users size={40} className="text-zinc-800 mx-auto mb-3" />
          <p className="text-zinc-500 font-semibold mb-1">{sporters.length === 0 ? "Nog geen sporters" : "Geen resultaten"}</p>
          {sporters.length === 0 && (
            <button onClick={() => setShowCreate(true)} className="mt-4 px-5 py-2.5 text-black font-bold text-sm rounded-xl" style={{ background: "#C9A84C" }}>
              Eerste sporter toevoegen
            </button>
          )}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl"
          style={{ background: 'var(--bc-card)', border: '1px solid var(--bc-border)' }}>
          <table className="w-full text-sm" style={{ background: 'var(--bc-card)' }}>
            <thead>
              <tr className="bc-table-header"
                style={{ background: 'var(--bc-card2)', borderBottom: '1px solid var(--bc-border)' }}>
                {["Naam", "Medewerker", "Status", "Voortgang", "Acties"].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-zinc-500 uppercase tracking-wide">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(s => {
                const hoofdProgress = s.progress?.[0];
                return (
                  <tr key={s.id} className="bc-table-row" onClick={() => setSelectedSporter(s)}
                    style={{ background: 'var(--bc-card)', borderBottom: '1px solid var(--bc-border)', cursor: 'pointer' }}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-black" style={{ background: "#C9A84C" }}>
                          {(s.naam || "?").charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="text-white font-medium text-sm">{s.naam}</p>
                          <p className="text-zinc-600 text-xs">{s.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {s.medewerker_naam
                        ? <span className="text-zinc-300 text-xs font-medium">{s.medewerker_naam}</span>
                        : <span className="text-zinc-600 text-xs">–</span>}
                    </td>
                    <td className="px-4 py-3"><StatusBadge status={s.overall_status} /></td>
                    <td className="px-4 py-3 min-w-36">
                      {hoofdProgress
                        ? <ProgressBar count={hoofdProgress.count} doelstelling={hoofdProgress.doelstelling} />
                        : <span className="text-zinc-700 text-xs">–</span>}
                    </td>
                    <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                      <div className="flex items-center gap-1.5">
                        <button onClick={() => setSelectedSporter(s)} data-testid={`view-sporter-${s.id}`}
                          className="p-1.5 text-zinc-500 hover:text-[#C9A84C] transition-colors"><ChevronRight size={14} /></button>
                        <button onClick={() => setDeleteConfirm(s)} data-testid={`delete-sporter-${s.id}`}
                          className="p-1.5 text-zinc-600 hover:text-red-400 transition-colors"><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-zinc-600 text-xs mt-3">{filtered.length} sporter{filtered.length !== 1 ? "s" : ""}</p>

      {showCreate && <CreateSporterModal token={token} onClose={() => setShowCreate(false)} onCreated={load} />}
      {selectedSporter && (
        <SporterDetailPanel token={token} sporter={selectedSporter} trainers={trainers}
          onClose={() => setSelectedSporter(null)} onRefresh={load} />
      )}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-[#111] border border-white/10 rounded-2xl p-7 max-w-sm w-full">
            <h3 className="text-white font-bold text-lg mb-2">Sporter verwijderen</h3>
            <p className="text-zinc-400 text-sm mb-6">Weet je zeker dat je <strong className="text-white">{deleteConfirm.naam}</strong> wilt verwijderen?</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="flex-1 py-2.5 bg-zinc-800/50 border border-white/8 text-zinc-400 font-semibold text-sm rounded-xl">Annuleren</button>
              <button onClick={() => handleDelete(deleteConfirm.id)} className="flex-1 py-2.5 bg-red-900/40 border border-red-500/30 text-red-400 font-semibold text-sm rounded-xl">Verwijderen</button>
            </div>
          </div>
        </div>
      )}
      {toast && <Toast msg={toast.msg} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}
